import keyBy from 'lodash/keyBy';

import * as types from '../actions/actionTypes';
import { IcomplianceCheckList } from '../models';
import { IinitialState } from '../types/rootState';

export type ComplianceCheckListState = { [id: string]: IcomplianceCheckList };

/*
 * A row can have more than one mutation queued at once - rename then delete, say - and each commits
 * on its own. Counting them, rather than flagging a single boolean, stops the first commit declaring
 * the row settled while a later one is still waiting to be sent.
 */
const pendingFor = (item: IcomplianceCheckList | undefined): number => item?.pendingSyncCount ?? 0;
const settleOne = (item: IcomplianceCheckList): number => Math.max(0, pendingFor(item) - 1);

/*
 * A fetch that left before a local edit can still arrive after it, carrying the pre-edit row. Rows
 * changed locally since the fetch was issued keep their own value.
 */
const isStaleFor = (item: IcomplianceCheckList | undefined, requestedAt: number | undefined): boolean =>
    // >= not >: within the same millisecond the order is unknowable, and keeping the engineer's edit
    // is the safer bias - a stale local value corrects on the next fetch, a lost edit does not
    !!item && !!requestedAt && (item.lastLocalChangeAt ?? 0) >= requestedAt;

const touch = (item: IcomplianceCheckList) => ({ ...item, lastLocalChangeAt: Date.now() });

/**
 * MMG-4335: deficiencies carried into (or added during) a Repair job, keyed by row id (MMG-4460).
 * Rows are seeded server-side when the asset joins the job, so the FSE only fetches and mutates them.
 */
const complianceCheckListReducer = (
    state: ComplianceCheckListState = {},
    action: any
): ComplianceCheckListState => {
    switch (action.type) {
        /*
         * The job download refetches this list while edits may still be sitting in the offline
         * outbox, so a plain merge would undo them (MMG-4458). A row with anything still queued
         * keeps its local copy until every one of those calls commits or rolls back.
         */
        case types.SET_COMPLIANCE_CHECKLIST: {
            const incoming = keyBy(action.payload?.data || [], 'id') as ComplianceCheckListState;
            const merged: ComplianceCheckListState = { ...state };
            Object.keys(incoming).forEach(id => {
                const local = merged[id];
                const server = incoming[id];
                if (pendingFor(local) > 0 || isStaleFor(local, action.payload?.requestedAt) || !server) {
                    return;
                }
                merged[id] = server;
            });
            return merged;
        }
        case types.ADD_COMPLIANCE_CHECKLIST_ITEM:
            return { ...state, [action.item.id]: action.item };
        case types.ADD_COMPLIANCE_CHECKLIST_ITEM_COMMIT: {
            // the server caps the name, so take its stored row as the truth once the add lands
            const item = state[action.id];
            const stored = action.payload?.data;
            if (!item || !stored) {
                return state;
            }
            return { ...state, [action.id]: { ...item, ...stored, pendingSyncCount: pendingFor(item) } };
        }
        case types.ADD_COMPLIANCE_CHECKLIST_ITEM_ROLLBACK:
            return Object.fromEntries(Object.entries(state).filter(([id]) => id !== action.item.id));
        case types.UPDATE_COMPLIANCE_CHECKLIST_VERIFIED:
        case types.UPDATE_COMPLIANCE_CHECKLIST_VERIFIED_ROLLBACK: {
            const item = state[action.id];
            if (!item) {
                return state;
            }
            return { ...state, [action.id]: { ...item, verified: action.verified } };
        }
        case types.UPDATE_COMPLIANCE_CHECKLIST_NAME: {
            const item = state[action.id];
            if (!item) {
                return state;
            }
            return { ...state, [action.id]: { ...touch(item), name: action.name, pendingSyncCount: pendingFor(item) + 1 } };
        }
        case types.UPDATE_COMPLIANCE_CHECKLIST_NAME_ROLLBACK: {
            const item = state[action.id];
            if (!item) {
                return state;
            }
            return { ...state, [action.id]: { ...touch(item), name: action.name, pendingSyncCount: settleOne(item) } };
        }
        case types.DELETE_COMPLIANCE_CHECKLIST_ITEM: {
            const item = state[action.id];
            if (!item) {
                return state;
            }
            return { ...state, [action.id]: { ...touch(item), isDeleted: true, pendingSyncCount: pendingFor(item) + 1 } };
        }
        case types.DELETE_COMPLIANCE_CHECKLIST_ITEM_ROLLBACK: {
            const item = state[action.id];
            if (!item) {
                return state;
            }
            return { ...state, [action.id]: { ...touch(item), isDeleted: false, pendingSyncCount: settleOne(item) } };
        }
        case types.UPDATE_COMPLIANCE_CHECKLIST_NAME_COMMIT:
        case types.DELETE_COMPLIANCE_CHECKLIST_ITEM_COMMIT: {
            const item = state[action.id];
            if (!item) {
                return state;
            }
            return { ...state, [action.id]: { ...item, pendingSyncCount: settleOne(item) } };
        }
        case types.USER_LOGOUT_SUCCESS:
            return {};
        default:
            return state;
    }
};

export const selectComplianceCheckListForAsset = (
    state: IinitialState,
    jobID: string,
    installBaseID: string
): IcomplianceCheckList[] =>
    Object.values(state.complianceCheckList as ComplianceCheckListState)
        .filter(item => item.jobID === jobID && item.installBaseID === installBaseID && !item.isDeleted)
        .sort((a, b) => (a.createDate || '').localeCompare(b.createDate || ''));

export const hasUnverifiedComplianceCheckListItems = (
    state: IinitialState,
    jobID: string,
    installBaseID: string
): boolean => selectComplianceCheckListForAsset(state, jobID, installBaseID).some(item => !item.verified);

export default complianceCheckListReducer;
